import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";

import { api, setAuthToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Brand from "@/components/Brand";

export default function Activate() {
    const { token } = useParams();
    const nav = useNavigate();
    const { refreshMe } = useAuth();
    const [invite, setInvite] = useState(null);
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/invites/by-token/${token}`)
            .then(({ data }) => {
                setInvite(data);
                setName(data.name || data.client_name || "");
                setError(null);
            })
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [token]);

    async function submit(e) {
        e?.preventDefault();
        if (password.length < 8) {
            toast.error("Pick a password of 8 characters or more");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords don't match");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/invites/activate`, { token, password, name });
            setAuthToken(data.token);
            toast.success(`Welcome to ClientOps, ${data.user.name}`);
            await refreshMe();
            nav("/portal", { replace: true });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <Brand size="md" className="mb-8" />

                    {loading && (
                        <div className="flex items-center gap-2 text-sm text-[#5E636E]">
                            <Loader2 className="h-4 w-4 animate-spin" /> Checking your invite…
                        </div>
                    )}

                    {!loading && error && (
                        <div data-testid="activate-error-state">
                            <div className="grid h-10 w-10 place-items-center rounded-full bg-rose-100 text-rose-600">
                                <AlertTriangle className="h-5 w-5" />
                            </div>
                            <h1 className="font-display text-3xl tracking-tight font-medium mt-4">This invite is no longer valid</h1>
                            <p className="mt-2 text-sm text-[#5E636E]">{error}</p>
                            <p className="mt-4 text-sm text-[#5E636E]">
                                Ask your accountant to send a fresh invite link, or{" "}
                                <Link to="/login" className="text-[#171717] underline underline-offset-4">sign in</Link> if you already have an account.
                            </p>
                        </div>
                    )}

                    {!loading && invite && (
                        <>
                            <div className="co-label">Activate your portal</div>
                            <h1 className="font-display text-3xl tracking-tight font-medium mt-1">
                                Welcome to {invite.firm_name || "ClientOps"}
                            </h1>
                            <p className="mt-2 text-sm text-[#5E636E]">
                                {invite.client_names && invite.client_names.length > 1 ? (
                                    <>
                                        You&apos;ve been invited as the portal contact for{" "}
                                        <span className="font-medium text-[#0F1115]">{invite.client_names.length} entities</span>:
                                    </>
                                ) : (
                                    <>
                                        You&apos;ve been invited as the portal contact for{" "}
                                        <span className="font-medium text-[#0F1115]">{invite.client_name}</span>.
                                    </>
                                )}{" "}
                                Set a password to activate your account.
                            </p>
                            {invite.client_names && invite.client_names.length > 1 && (
                                <ul className="mt-3 rounded-md border border-[#E4E4E7] bg-[#FAFAFA] divide-y divide-[#E4E4E7]" data-testid="activate-entity-list">
                                    {invite.client_names.map((nm) => (
                                        <li key={nm} className="px-3 py-1.5 text-xs text-[#0F1115]">· {nm}</li>
                                    ))}
                                </ul>
                            )}

                            <form className="mt-8 space-y-4" onSubmit={submit} data-testid="activate-form">
                                <div className="space-y-1.5">
                                    <Label>Sign-in email</Label>
                                    <Input value={invite.email} disabled className="font-mono" data-testid="activate-email" />
                                    {invite.email_updated && (
                                        <p className="text-[11px] text-[#5E636E]" data-testid="activate-email-updated">
                                            Updated from your accountant&apos;s Practice Manager. Previous address on the invite email was <span className="font-mono">{invite.invite_email}</span>.
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="name">Your name</Label>
                                    <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" required data-testid="activate-name" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="pw">New password</Label>
                                    <Input id="pw" type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" required data-testid="activate-password" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="confirm">Confirm password</Label>
                                    <Input id="confirm" type="password" minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="activate-confirm" />
                                </div>
                                <Button type="submit" className="w-full h-11 bg-[#171717] hover:bg-[#2E2E2E] text-white" disabled={busy} data-testid="activate-submit">
                                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Activating…</> : <><CheckCircle2 className="h-4 w-4" /> Activate &amp; sign in <ArrowRight className="h-4 w-4" /></>}
                                </Button>
                                <p className="text-[11px] text-[#5E636E]">
                                    By activating you agree to use this portal for sending documents and messages to your accountant.
                                </p>
                            </form>
                        </>
                    )}
                </div>
            </div>

            <div className="hidden lg:flex bg-[#0F1115] text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-dot-grid opacity-[0.08]" />
                <div className="relative z-10 m-auto max-w-md px-12">
                    <div className="co-label !text-white/60 mb-3">Your portal in one minute</div>
                    <ul className="space-y-5 text-sm text-white/80 leading-relaxed">
                        <li><span className="font-display text-white text-base font-medium block">Upload, don&apos;t email.</span> Bank statements, receipts, payroll — sent straight to your accountant.</li>
                        <li><span className="font-display text-white text-base font-medium block">See what&apos;s outstanding.</span> No more guessing what they still need from you.</li>
                        <li><span className="font-display text-white text-base font-medium block">Get answers, fast.</span> Searchable help guides and a direct line to your team.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
